import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    LayoutDashboard,
    Users,
    Tag,
    BookOpen,
    Settings,
    LogOut,
    X,
    BarChart3,
    Building2,
    CreditCard,
    Bell,
    User,
    Shield,
    Package
} from 'lucide-react'
import { useAdminAuth } from '../contexts/AdminAuthContext'

interface AdminLayoutProps {
    children: React.ReactNode
}

export default function AdminLayout({ children }: AdminLayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const { admin, logout } = useAdminAuth()
    const location = useLocation()
    const navigate = useNavigate()

    const handleLogout = async () => {
        await logout()
        navigate('/admin/login')
    }

    const navigation = [
        {
            name: 'Dashboard',
            href: '/admin/dashboard',
            icon: LayoutDashboard,
            current: location.pathname === '/admin/dashboard'
        },
        {
            name: 'Usuários',
            href: '/admin/usuarios',
            icon: Users,
            current: location.pathname === '/admin/usuarios'
        },
        {
            name: 'Cupons',
            href: '/admin/cupons',
            icon: Tag,
            current: location.pathname === '/admin/cupons'
        },
        {
            name: 'Cursos',
            href: '/admin/cursos',
            icon: BookOpen,
            current: location.pathname === '/admin/cursos'
        },
        {
            name: 'Vendas',
            href: '/admin/vendas',
            icon: BarChart3,
            current: location.pathname === '/admin/vendas'
        },
        {
            name: 'Imóveis',
            href: '/admin/imoveis',
            icon: Building2,
            current: location.pathname === '/admin/imoveis'
        },
        {
            name: 'Assinaturas',
            href: '/admin/assinaturas',
            icon: CreditCard,
            current: location.pathname === '/admin/assinaturas'
        },
        {
            name: 'Planos',
            href: '/admin/planos',
            icon: Package,
            current: location.pathname === '/admin/planos'
        },
        {
            name: 'Notificações',
            href: '/admin/notificacoes',
            icon: Bell,
            current: location.pathname === '/admin/notificacoes'
        },
        {
            name: 'Configurações',
            href: '/admin/configuracoes',
            icon: Settings,
            current: location.pathname === '/admin/configuracoes'
        }
    ]

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
            {/* Sidebar Mobile */}
            <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
                <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                        <button
                            type="button"
                            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="h-6 w-6 text-white" />
                        </button>
                    </div>
                    <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                        <div className="flex-shrink-0 flex items-center px-4">
                            <div className="flex items-center">
                                <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                                    Admin Panel
                                </span>
                            </div>
                        </div>
                        <nav className="mt-5 px-2 space-y-1">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${item.current
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                        }`}
                                >
                                    <item.icon
                                        className={`mr-4 h-6 w-6 ${item.current
                                            ? 'text-blue-500 dark:text-blue-400'
                                            : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                                            }`}
                                    />
                                    {item.name}
                                </Link>
                            ))}
                        </nav>
                    </div>
                    {/* Admin Info */}
                    <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <User className="h-8 w-8 text-gray-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-base font-medium text-gray-700 dark:text-gray-300">
                                    {admin?.nome}
                                </p>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sidebar Desktop */}
            <div className="hidden lg:flex lg:flex-shrink-0 lg:fixed lg:inset-y-0 lg:left-0 lg:z-40">
                <div className="flex flex-col w-64">
                    <div className="flex flex-col h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
                        <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                            <div className="flex items-center flex-shrink-0 px-4">
                                <div className="flex items-center">
                                    <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                    <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                                        Admin Panel
                                    </span>
                                </div>
                            </div>
                            <nav className="mt-5 flex-1 px-2 space-y-1">
                                {navigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${item.current
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                    >
                                        <item.icon
                                            className={`mr-3 h-5 w-5 ${item.current
                                                ? 'text-blue-500 dark:text-blue-400'
                                                : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                                                }`}
                                        />
                                        {item.name}
                                    </Link>
                                ))}
                            </nav>
                        </div>
                        {/* Admin Info */}
                        <div className="flex-shrink-0 flex border-t border-gray-200 dark:border-gray-700 p-4">
                            <div className="flex items-center w-full">
                                <div className="flex-shrink-0">
                                    <User className="h-8 w-8 text-gray-400" />
                                </div>
                                <div className="ml-3 flex-1">
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {admin?.nome}
                                    </p>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                        {admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                                    </p>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="ml-2 flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                >
                                    <LogOut className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-col flex-1 min-w-0 lg:ml-64">
                {/* Top Bar - TEMPORARIAMENTE COMENTADO PARA TESTE */}
                {/* <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        className="px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <Menu className="h-6 w-6" />
                    </button>
                    <div className="flex-1 px-4 flex justify-end">
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Olá, {admin?.nome}
                            </span>
                            <button
                                onClick={handleLogout}
                                className="bg-gray-100 dark:bg-gray-700 p-1 rounded-full text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                            >
                                <LogOut className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div> */}

                {/* Page Content */}
                <main className="flex-1 bg-gray-50 dark:bg-gray-900 p-4 lg:p-6">
                    {children}
                </main>
            </div>
        </div>
    )
}
